import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/privacy.scss";
import "../css/policies.scss";

const POLICY_EFFECTIVE_DATE = "OCTOBER 19, 2026";

const EmailLink = ({ subject, children = siteConfig.contact.email }) => {
  const suffix = subject ? `?subject=${encodeURIComponent(subject)}` : "";
  return <a href={`mailto:${siteConfig.contact.email}${suffix}`}>{children}</a>;
};

const legalPolicies = [
  {
    slug: "payment",
    navTitle: "Payment & billing",
    title: ["PAYMENT &", "BILLING"],
    summary: "How payment authorization, billing, currency, fraud checks, and refunds are handled.",
    intro: "The payment methods currently available for an order are the methods displayed at checkout. Availability can vary by device, destination, order value, and payment provider.",
    sections: [
      {
        title: "PRICES & CURRENCY",
        content: <>
          <p>Store prices are displayed in {siteConfig.commerce.currency} unless the page or checkout states otherwise. Your card issuer or wallet provider may convert the charge into another currency and may add its own conversion or cross-border fee.</p>
          <p>Product prices do not include delivery, import charges, or taxes unless those amounts are expressly included or shown in the order summary. Review the final total before submitting payment.</p>
        </>,
      },
      {
        title: "AUTHORIZATION & CAPTURE",
        content: <>
          <p>When you submit an order, your payment provider may authorize the total before the order is accepted. An authorization can appear as pending even if an order is later canceled. The provider controls when a released authorization disappears.</p>
          <p>We may capture payment when the order is placed, when fulfillment begins, or in separate amounts when an order ships in parts. You authorize the amount displayed at checkout, subject to any clearly disclosed adjustment you approve.</p>
        </>,
      },
      {
        title: "PAYMENT SECURITY",
        content: <>
          <p>Payments are handled by payment service providers using their own security controls. We do not intentionally store complete card numbers or card security codes in the storefront.</p>
          <p>Never send full card details by email or through the contact form. We may request order details or identity verification, but we will not ask you to email your password or complete card number.</p>
        </>,
      },
      {
        title: "DECLINES, CHECKS & DUPLICATES",
        content: <>
          <p>A payment may be declined or held because of incorrect billing details, issuer rules, geographic restrictions, fraud screening, insufficient funds, or a technical error. Your bank or payment provider can explain an issuer decline.</p>
          <p>If you see more than one pending amount, first compare the amounts and order confirmations. One entry may be a temporary authorization. Contact us before disputing a valid order so we can investigate promptly.</p>
        </>,
      },
      {
        title: "REFUNDS & CHARGEBACKS",
        content: <>
          <p>Approved refunds are normally returned to the original payment method. Processing time after we issue a refund depends on the payment provider. Store credit or another method will be used only when agreed or required by law.</p>
          <p>A chargeback does not expand return or warranty rights. Fraudulent chargebacks may result in order cancellation or account restrictions. Nothing here limits a right to dispute a genuinely unauthorized or incorrectly processed charge.</p>
        </>,
      },
      {
        title: "BILLING HELP",
        content: <p>For a billing question, email <EmailLink subject="Billing question" /> with your order number and a description of the issue. Do not include full card details.</p>,
      },
    ],
  },
  {
    slug: "warranty",
    navTitle: "Limited warranty",
    title: ["LIMITED", "WARRANTY"],
    summary: "Coverage for eligible hardware defects and the process for making a warranty claim.",
    intro: "Unless a product page states a different period, eligible hardware purchased directly from this store carries a one-year limited warranty from the original purchase date. Mandatory consumer guarantees remain unaffected.",
    sections: [
      {
        title: "WHO & WHAT IS COVERED",
        content: <>
          <p>The limited warranty applies to the original retail purchaser of a genuine {siteConfig.wordmark} product bought from this store or an authorized seller, with valid proof of purchase.</p>
          <p>It covers defects in materials or workmanship that arise during normal, intended use within the stated warranty period. Coverage applies only to the product and components originally supplied with it.</p>
        </>,
      },
      {
        title: "WHAT IS NOT COVERED",
        content: <>
          <p>The limited warranty does not cover:</p>
          <ul>
            <li>normal wear, cosmetic changes, battery aging, or consumable parts;</li>
            <li>accident, liquid, impact, misuse, neglect, improper cleaning, or unsuitable storage;</li>
            <li>incorrect installation, incompatible power, unauthorized repair, alteration, or disassembly;</li>
            <li>lost or stolen items, removed serial labels, counterfeit items, or products sold as final-sale samples with disclosed defects; or</li>
            <li>damage caused by third-party products, carrier handling, or use outside published instructions.</li>
          </ul>
        </>,
      },
      {
        title: "HOW TO MAKE A CLAIM",
        content: <>
          <ol>
            <li>Email <EmailLink subject="Warranty claim" /> within the warranty period.</li>
            <li>Include the order number, product name, serial number if present, a clear description, and useful photos or video.</li>
            <li>Follow the troubleshooting and return instructions we provide. Do not send a product before receiving authorization.</li>
          </ol>
          <p>You may need to provide proof of purchase and return the product for inspection. Back up any settings where possible because service can reset or replace the product.</p>
        </>,
      },
      {
        title: "AVAILABLE REMEDIES",
        content: <>
          <p>For a valid claim, we may repair the product, replace it with the same or a functionally comparable product, or refund the purchase price. The appropriate remedy depends on the issue, stock, cost, and applicable law.</p>
          <p>Replacement parts or products may be new or professionally restored where lawful. Unless local law requires more, repaired or replacement products are covered for the remainder of the original warranty period or 90 days, whichever is longer.</p>
        </>,
      },
      {
        title: "SHIPPING & DATA",
        content: <>
          <p>We will explain approved-claim shipping responsibilities before a return. Improperly packed, unapproved, or cash-on-delivery parcels may be refused. We are not responsible for data or settings stored on a returned product.</p>
          <div className="policy-page__note"><strong>YOUR LEGAL RIGHTS</strong><p>This voluntary warranty is additional to rights that cannot be excluded under the consumer law that applies to you.</p></div>
        </>,
      },
    ],
  },
  {
    slug: "orders",
    navTitle: "Order changes & cancellations",
    title: ["ORDER CHANGES", "& CANCELLATIONS"],
    summary: "When an order can be changed, canceled, limited, or refused.",
    intro: "Fulfillment can begin soon after checkout. Contact support immediately if an address, item, or order needs attention, but changes are not guaranteed after submission.",
    sections: [
      {
        title: "ORDER RECEIPT & ACCEPTANCE",
        content: <p>An automated confirmation means we received your order request. It is not acceptance. We accept an order when fulfillment begins, shipment is confirmed, or we otherwise state that the order has been accepted.</p>,
      },
      {
        title: "CUSTOMER REQUESTS",
        content: <>
          <p>Before warehouse processing begins, we may be able to correct an address, remove an item, or cancel an order. We generally cannot change products, variants, quantities, discounts, or payment methods on an accepted order; cancellation and a new order may be required.</p>
          <p>Once an order has entered packing or carrier processing, it cannot normally be changed or canceled. Eligible items may still be returned under our <Link to="/returns">Return Policy</Link>.</p>
        </>,
      },
      {
        title: "OUR RIGHT TO CANCEL OR LIMIT",
        content: <>
          <p>We may cancel or limit an order for stock errors, pricing errors, failed payment, suspected fraud, sanctions or delivery restrictions, purchase limits, resale activity, technical errors, or a breach of store terms.</p>
          <p>If we cancel after receiving payment, we will issue the appropriate refund. We may contact you to verify identity, payment authority, or delivery details before deciding whether to fulfill an order.</p>
        </>,
      },
      {
        title: "ADDRESS RESPONSIBILITY",
        content: <p>You are responsible for checking the recipient, building or unit, city, postal code, country, and contact details. An address change requested after dispatch is controlled by the carrier and may incur a fee or be unavailable.</p>,
      },
      {
        title: "HOW TO REQUEST A CHANGE",
        content: <p>Email <EmailLink subject="Urgent order change" /> with “Urgent order change” and your order number in the subject line. Do not place a duplicate order unless you are prepared for both orders to be processed.</p>,
      },
    ],
  },
  {
    slug: "preorders",
    navTitle: "Preorders & backorders",
    title: ["PREORDERS &", "BACKORDERS"],
    summary: "Payment, estimates, delays, split shipments, and cancellation rules for future stock.",
    intro: "A preorder reserves stock expected in a future production or delivery window. A backorder is an order for a standard item that is temporarily unavailable. The product page identifies either status where applicable.",
    sections: [
      {
        title: "ESTIMATED AVAILABILITY",
        content: <p>Release and shipping windows are good-faith estimates, not guaranteed dates. Manufacturing, quality review, inbound freight, customs, and events outside reasonable control can change the estimate. We will provide a material-delay update using the order contact details where practical.</p>,
      },
      {
        title: "PAYMENT TIMING",
        content: <p>Unless checkout states otherwise, a preorder may be charged in full when placed. Your payment statement can therefore show the charge before shipment. Taxes, delivery charges, and currency conversion follow the terms shown at checkout.</p>,
      },
      {
        title: "MIXED ORDERS",
        content: <p>An order containing available and preorder items may ship together when all items are ready or in separate parcels. The approach shown at checkout or in the order confirmation controls. We will not add a delivery charge solely because we choose to split shipment.</p>,
      },
      {
        title: "CHANGES & CANCELLATION",
        content: <p>Contact us promptly to request cancellation. A standard preorder can generally be canceled before fulfillment begins, but personalized goods, clearly disclosed final-sale items, or committed production may have different rules where lawful. Statutory cancellation rights still apply.</p>,
      },
      {
        title: "ALLOCATION & SHORTFALLS",
        content: <p>If supply is lower than reasonably expected, we may allocate available units fairly, apply published purchase limits, offer an alternative only with your consent, or cancel and refund the affected item. We do not promise resale value or future availability.</p>,
      },
    ],
  },
  {
    slug: "promotions",
    navTitle: "Promotions & discounts",
    title: ["PROMOTIONS &", "DISCOUNTS"],
    summary: "Rules for codes, discounts, bundles, gifts, and price adjustments.",
    intro: "A promotion may include additional terms in the advertisement, email, product page, or checkout. Those specific terms apply together with this policy.",
    sections: [
      {
        title: "PROMOTION ELIGIBILITY",
        content: <p>Promotions are valid only during the stated period, in eligible regions, for eligible products, and while supplies last. Unless stated otherwise, codes are limited to one per customer and order, cannot be combined, and have no cash value.</p>,
      },
      {
        title: "EXCLUSIONS & LIMITS",
        content: <p>New releases, collaborations, preorders, gift cards, bundles, already-discounted items, taxes, duties, and delivery charges may be excluded. Minimum spend is calculated after excluded products and before tax or delivery unless the offer states otherwise.</p>,
      },
      {
        title: "RETURNS & FREE GIFTS",
        content: <p>A refund reflects the amount actually paid and any discount allocated to the returned item. If a return makes an order ineligible for a free gift, bundle price, or threshold offer, the gift must also be returned or its disclosed value may be deducted where lawful.</p>,
      },
      {
        title: "ERRORS, ABUSE & RESALE",
        content: <p>We may reject or reverse a promotion affected by a clear publishing or technical error, automated use, account duplication, misrepresentation, resale, or other abuse. If payment was taken, we will refund any canceled amount.</p>,
      },
      {
        title: "PRICE ADJUSTMENTS",
        content: <p>Price changes are not retroactive unless a particular offer says so or law requires it. A discount must be applied before checkout is completed. Support cannot guarantee a replacement code for an expired, mistyped, or ineligible offer.</p>,
      },
    ],
  },
  {
    slug: "gift-cards",
    navTitle: "Gift cards & store credit",
    title: ["GIFT CARDS &", "STORE CREDIT"],
    summary: "Terms that apply if digital gift cards or store credit are offered.",
    intro: "These terms apply only when a gift card or store credit is made available by this store. Availability, denominations, and eligible destinations will be shown at purchase or issue.",
    sections: [
      {
        title: "USE",
        content: <p>Gift cards and store credit can be used only on this store for eligible products and charges. They are not credit or debit cards. A remaining balance may be used later, subject to any lawful expiration disclosure made when issued.</p>,
      },
      {
        title: "PURCHASES & REFUNDS",
        content: <p>A gift card is normally non-refundable and cannot be redeemed for cash except where law requires. A refund for an order paid with a gift card is returned to the same gift card or equivalent store credit unless mandatory law requires another method.</p>,
      },
      {
        title: "DELIVERY & RISK",
        content: <p>Digital delivery is sent to the address entered by the purchaser. Check it carefully. Treat the code like cash: anyone with the code may be able to use it. We may replace an unused balance only when ownership and purchase can be verified.</p>,
      },
      {
        title: "RESTRICTIONS",
        content: <p>Gift cards may not be resold, used to purchase other gift cards, redeemed through an unauthorized platform, or used for unlawful activity. We may suspend a balance linked to fraud, unauthorized payment, or policy abuse while it is investigated.</p>,
      },
      {
        title: "HELP",
        content: <p>For balance or delivery help, email <EmailLink subject="Gift card help" /> with the purchaser email and order number. Never post a gift card code publicly.</p>,
      },
    ],
  },
  {
    slug: "taxes-duties",
    navTitle: "Taxes, duties & imports",
    title: ["TAXES, DUTIES", "& IMPORTS"],
    summary: "How destination taxes, customs, brokerage, and import responsibility are handled.",
    intro: "Tax and import treatment depends on the destination, the products, local thresholds, and the delivery service. Checkout information is based on the address and rules available when the order is placed.",
    sections: [
      {
        title: "CHECKOUT TAX",
        content: <p>Where we are required or able to collect tax, the amount is calculated using the delivery address and taxable total. Estimates may change if the address, order, or applicable rate changes before acceptance. Tax-exempt purchasing is available only if expressly supported and approved before checkout.</p>,
      },
      {
        title: "DUTIES-PAID & DUTIES-UNPAID",
        content: <p>If checkout expressly says duties are included, the stated order total includes the covered import charges. Otherwise, the shipment is duties unpaid and the recipient is responsible for customs duty, import tax, brokerage, handling, and local collection fees.</p>,
      },
      {
        title: "CUSTOMS INFORMATION",
        content: <p>We declare the actual product description and transaction value as required. We will not mark a commercial order as a gift or deliberately understate its value. Customs authorities may open or delay a parcel and may contact the recipient for identification or documentation.</p>,
      },
      {
        title: "REFUSAL & RETURNS",
        content: <p>Refusing an import charge does not cancel the order automatically. If the carrier returns the parcel, any approved refund may be reduced by non-refundable delivery, return, storage, brokerage, or duty costs where permitted by law.</p>,
      },
      {
        title: "CUSTOMER RESPONSIBILITY",
        content: <p>Before ordering, confirm that the product can be lawfully imported and used at the destination. We cannot provide personal tax or customs advice. Your local customs or tax authority is the best source for destination-specific rules.</p>,
      },
    ],
  },
  {
    slug: "accessibility",
    navTitle: "Accessibility statement",
    title: ["ACCESSIBILITY", "STATEMENT"],
    summary: "Our commitment to accessible shopping and how to report a barrier or request help.",
    intro: `${siteConfig.wordmark} aims to make its online store usable by as many people as reasonably possible, including people who use assistive technologies or alternative input methods.`,
    sections: [
      {
        title: "OUR APPROACH",
        content: <p>We work to support clear structure, keyboard access, meaningful text alternatives, readable contrast, responsive layouts, understandable forms, and compatibility with common browsers and assistive technologies. Accessibility is an ongoing process as content and services change.</p>,
      },
      {
        title: "KNOWN LIMITS",
        content: <p>Some third-party payment, delivery, media, or support tools may be outside our direct control. Product imagery may also contain decorative text that is not essential to completing a purchase. We seek practical alternatives when a barrier is identified.</p>,
      },
      {
        title: "GET ASSISTANCE",
        content: <p>If any content or checkout function is difficult to use, email <EmailLink subject="Accessibility support" /> with “Accessibility” in the subject. Describe the page, task, device, browser, and assistive technology if you are comfortable doing so.</p>,
      },
      {
        title: "ALTERNATIVE ACCESS",
        content: <p>We will make reasonable efforts to provide the relevant information in another format or help complete the intended task. Support is available during {siteConfig.contact.hours}; response times may vary across weekends and public holidays.</p>,
      },
      {
        title: "FEEDBACK",
        content: <p>Accessibility feedback is welcome. We review actionable reports as part of store maintenance and prioritize issues that prevent browsing, understanding product information, or completing a transaction.</p>,
      },
    ],
  },
  {
    slug: "intellectual-property",
    navTitle: "Intellectual property notices",
    title: ["INTELLECTUAL", "PROPERTY"],
    summary: "Ownership, permitted use, third-party rights, and a process for reporting infringement.",
    intro: "We respect intellectual-property rights and expect store users, contributors, suppliers, and partners to do the same. This policy explains permitted use and how a rights holder can submit a concern.",
    sections: [
      {
        title: "STORE MATERIALS",
        content: <p>Unless otherwise identified, the store’s original text, graphics, interface, code, product names, photography, and designs are owned by or lawfully licensed to the store operator. You may view and use them for personal shopping and product support, but may not reproduce, sell, scrape, republish, or create commercial derivatives without permission.</p>,
      },
      {
        title: "THIRD-PARTY RIGHTS",
        content: <p>Third-party names, marks, artwork, and other materials remain the property of their respective owners. A factual compatibility reference does not imply sponsorship or affiliation. We do not knowingly claim ownership of another party’s work.</p>,
      },
      {
        title: "REPORTING INFRINGEMENT",
        content: <>
          <p>A rights holder or authorized representative can email <EmailLink subject="Intellectual property notice" /> with:</p>
          <ul>
            <li>contact details and identification of the protected work, mark, or right;</li>
            <li>the exact store URL and a clear description of the material at issue;</li>
            <li>the basis for ownership or authorization to act;</li>
            <li>a good-faith statement that the reported use is not authorized; and</li>
            <li>a statement that the notice is accurate, plus a physical or electronic signature.</li>
          </ul>
        </>,
      },
      {
        title: "REVIEW & RESPONSE",
        content: <p>We may request additional information, notify the affected contributor, restrict material while reviewing it, or remove material where appropriate. Knowingly false or materially misleading notices may create liability. Nothing in this process decides ownership or waives any legal defense.</p>,
      },
      {
        title: "COUNTER-NOTICES",
        content: <p>If your submitted material is restricted and you believe that resulted from error or lawful use, reply with the identified material, your contact details, the reason for your position, and any legally required statements. We will evaluate the response under the law applicable to the notice.</p>,
      },
    ],
  },
  {
    slug: "user-content",
    navTitle: "Reviews & user content",
    title: ["REVIEWS &", "USER CONTENT"],
    summary: "Standards and permissions for reviews, images, messages, and other submitted material.",
    intro: "This policy applies when the store allows reviews, questions, photos, social posts, contest entries, or other customer-submitted material. It does not require us to publish every submission.",
    sections: [
      {
        title: "YOUR RESPONSIBILITY",
        content: <p>You must have the right to submit the material. It must reflect a genuine experience and must not contain confidential information, unlawful content, malware, undisclosed paid endorsements, impersonation, harassment, or material that infringes another person’s rights.</p>,
      },
      {
        title: "LICENSE TO OPERATE THE STORE",
        content: <p>You keep ownership of your content. You grant us a non-exclusive, worldwide, royalty-free license to host, reproduce, format, display, translate, and distribute it for store operation, customer support, moderation, and promotion. This license lasts while the content is used, subject to applicable privacy and deletion rights.</p>,
      },
      {
        title: "MODERATION",
        content: <p>We may review, reject, label, shorten for formatting, disable, or remove content that violates this policy or is irrelevant, duplicative, unsafe, or deceptive. We do not promise to monitor all content and do not endorse every opinion submitted by a user.</p>,
      },
      {
        title: "INCENTIVIZED REVIEWS",
        content: <p>If a review was submitted after a free product, discount, sweepstakes entry, or other incentive, that relationship must be clearly disclosed. An incentive cannot require a positive opinion. We may add a disclosure label where appropriate.</p>,
      },
      {
        title: "REPORT OR REMOVE CONTENT",
        content: <p>To report unlawful or infringing material, use our <Link to="/policies/intellectual-property">Intellectual Property Policy</Link>. For another content concern or a deletion request, email <EmailLink subject="User content request" /> with the relevant URL and details.</p>,
      },
    ],
  },
  {
    slug: "security",
    navTitle: "Security & responsible disclosure",
    title: ["SECURITY &", "DISCLOSURE"],
    summary: "How to report a suspected security issue without putting customers or systems at risk.",
    intro: "We welcome good-faith reports that help protect customers and the store. This is a disclosure channel, not a promise of a reward or a public bug-bounty program.",
    sections: [
      {
        title: "REPORTING",
        content: <p>Email <EmailLink subject="Security report" /> with “Security report” in the subject. Include the affected URL or feature, steps to reproduce, observed impact, relevant timestamps, and a safe proof of concept. Do not include personal data that is unnecessary to explain the issue.</p>,
      },
      {
        title: "SAFE RESEARCH",
        content: <p>Use only accounts and data you own or have permission to test. Stop if you encounter another person’s data. Do not disrupt service, degrade availability, send spam, conduct denial-of-service testing, install malware, access physical facilities, or use social engineering.</p>,
      },
      {
        title: "DATA HANDLING",
        content: <p>Do not download, retain, alter, or publicly disclose customer data. If limited access occurs accidentally, stop, record only what is necessary to identify the problem, report it promptly, and securely delete any local copy after we confirm receipt.</p>,
      },
      {
        title: "OUR RESPONSE",
        content: <p>We aim to acknowledge a useful report, investigate proportionately, and communicate when we can. Remediation timing depends on severity and complexity. We may ask for clarification and may decline reports about unsupported software, theoretical issues, or findings without security impact.</p>,
      },
      {
        title: "GOOD-FAITH CONDUCT",
        content: <p>We will not intentionally pursue a claim against research that follows this policy, avoids harm, and complies with applicable law. This statement cannot authorize activity against third-party systems or waive rights belonging to another person.</p>,
      },
    ],
  },
  {
    slug: "product-safety",
    navTitle: "Product safety & recalls",
    title: ["PRODUCT SAFETY", "& RECALLS"],
    summary: "Safe use, incident reporting, firmware care, and recall communications.",
    intro: "Use products only for their intended purpose and follow the instructions supplied with them. Product information on the store does not replace warnings or safety instructions packaged with the product.",
    sections: [
      {
        title: "SAFE USE",
        content: <p>Inspect the product, cable, connector, and power source before use. Keep electronics away from liquid, extreme heat, impact, small children, and unsuitable voltage. Stop using a product that becomes unusually hot, swollen, damaged, emits odor or smoke, or exposes internal wiring.</p>,
      },
      {
        title: "MODIFICATIONS & PARTS",
        content: <p>Use compatible parts and follow published installation guidance. Modification, disassembly, or third-party components can create safety risks and may affect voluntary warranty coverage, although this does not remove rights that cannot legally be excluded.</p>,
      },
      {
        title: "FIRMWARE & SOFTWARE",
        content: <p>Install official firmware only from a source identified by us, keep a device connected as instructed during updates, and review release notes. We are not responsible for third-party firmware or tools presented as official without authorization.</p>,
      },
      {
        title: "REPORT AN INCIDENT",
        content: <p>For a suspected safety issue, stop use and email <EmailLink subject="Urgent product safety report" />. Include the product, order number, serial number, date, circumstances, and photos if safe. For an immediate risk of injury, fire, or electrical danger, contact local emergency services first.</p>,
      },
      {
        title: "RECALLS & SAFETY NOTICES",
        content: <p>If a product requires corrective action, we may publish a notice and contact known purchasers using order information. Follow the notice even if the product appears normal. Remedies and disposal instructions will depend on the issue and applicable law.</p>,
      },
    ],
  },
  {
    slug: "communications",
    navTitle: "Electronic communications",
    title: ["ELECTRONIC", "COMMUNICATIONS"],
    summary: "Rules for order messages, marketing choices, consent, and unsubscribe requests.",
    intro: "We use electronic communications to operate the store, support orders, and—where permitted—share marketing. Transactional and marketing messages have different purposes and controls.",
    sections: [
      {
        title: "TRANSACTIONAL MESSAGES",
        content: <p>By placing an order or requesting support, you agree to receive necessary messages about payment, fraud review, fulfillment, delivery, returns, warranty, security, or safety. These are not marketing messages, and opting out of marketing does not stop them.</p>,
      },
      {
        title: "MARKETING CONSENT",
        content: <p>We send promotional email only where permitted by law, based on consent or another valid legal basis. Consent is not a condition of purchase. Any sign-up form may include additional terms that apply to that channel or campaign.</p>,
      },
      {
        title: "UNSUBSCRIBE & PREFERENCES",
        content: <p>Use the unsubscribe link in a marketing email or contact us to change your preference. A request may take a short period to process, and we may retain a minimal suppression record so we can honor the choice.</p>,
      },
      {
        title: "MESSAGE DELIVERY",
        content: <p>You are responsible for providing a current address and checking spam or filtering settings. Delivery is not guaranteed because network, provider, device, or account settings may block or delay a message.</p>,
      },
      {
        title: "PRIVACY",
        content: <p>How contact information is collected, used, shared, and retained is explained in our <Link to="/privacy">Privacy Policy</Link>. Questions or preference requests can be sent to <EmailLink subject="Communication preferences" />.</p>,
      },
    ],
  },
  {
    slug: "final-sale",
    navTitle: "Final sale & clearance",
    title: ["FINAL SALE &", "CLEARANCE"],
    summary: "How clearly marked final-sale, clearance, sample, and open-box products are treated.",
    intro: "A product is final sale only when that status is clearly shown before purchase. A discount by itself does not necessarily make an item final sale.",
    sections: [
      {
        title: "FINAL-SALE ITEMS",
        content: <p>Except where law requires otherwise, a clearly marked final-sale item cannot be returned for change of mind. Examples may include personalized products, gift cards, hygiene-sensitive consumables, samples, or discontinued clearance stock.</p>,
      },
      {
        title: "OPEN-BOX & SAMPLE CONDITION",
        content: <p>Open-box or sample products may show packaging damage or cosmetic wear described on the product page. Any disclosed condition is part of the purchase. A material condition not disclosed before purchase should be reported promptly.</p>,
      },
      {
        title: "DEFECTS & WRONG ITEMS",
        content: <p>Final-sale status does not remove a remedy that cannot be excluded by law. If an item arrives materially different from its description, damaged in transit, incorrect, or covered by a mandatory consumer guarantee, contact us under the <Link to="/returns">Return Policy</Link>.</p>,
      },
      {
        title: "PROMOTIONS",
        content: <p>Clearance and final-sale products may be excluded from additional discounts, bundles, price adjustments, or free-gift thresholds. The specific promotion terms control.</p>,
      },
      {
        title: "CHECK BEFORE ORDERING",
        content: <p>Confirm compatibility, layout, color, condition, and final-sale labeling before checkout. Questions can be sent to <EmailLink subject="Final sale product question" /> before the order is placed.</p>,
      },
    ],
  },
  {
    slug: "availability",
    navTitle: "Availability & substitutions",
    title: ["AVAILABILITY &", "SUBSTITUTIONS"],
    summary: "Stock accuracy, purchase limits, discontinued items, and product changes.",
    intro: "Inventory changes quickly, especially during launches and promotions. Adding an item to a bag or wishlist does not reserve it.",
    sections: [
      {
        title: "STOCK INFORMATION",
        content: <p>We work to keep availability accurate, but simultaneous orders, damaged stock, count errors, or system delays can make an item unavailable after checkout. If we cannot fulfill it, we may offer a choice where practical or cancel and refund the affected amount.</p>,
      },
      {
        title: "PURCHASE LIMITS",
        content: <p>We may apply reasonable per-customer, household, address, payment-method, or order limits. Related orders may be combined for limit enforcement. Limits help manage scarce inventory and discourage unauthorized resale.</p>,
      },
      {
        title: "NO MATERIAL SUBSTITUTION WITHOUT CONSENT",
        content: <p>We will not knowingly replace an ordered product with a materially different item without your agreement. Packaging, included printed material, or minor production details may change when function and core specifications are not materially reduced.</p>,
      },
      {
        title: "DISCONTINUED PRODUCTS",
        content: <p>Products, colors, layouts, and accessories may be changed or discontinued without advance notice. We do not guarantee restocks, continued software support beyond stated commitments, or compatibility with products released later.</p>,
      },
      {
        title: "NOTIFICATIONS",
        content: <p>A restock or wishlist message is informational and does not reserve inventory or guarantee price. Availability is confirmed only when an order is accepted.</p>,
      },
    ],
  },
  {
    slug: "authenticity",
    navTitle: "Authenticity & authorized sellers",
    title: ["AUTHENTICITY &", "AUTHORIZED SALES"],
    summary: "How direct purchases, third-party sellers, counterfeits, and proof of purchase are handled.",
    intro: "Products purchased directly from this store are supplied through our own sales channel. Products offered elsewhere are governed by the seller’s identity, authorization status, and policies.",
    sections: [
      {
        title: "AUTHORIZED PURCHASES",
        content: <p>Keep the dated receipt or order confirmation. An authorized seller should identify itself on the receipt and provide applicable return information. Marketplace listings are not automatically authorized merely because they use our name or product photos.</p>,
      },
      {
        title: "THIRD-PARTY SELLERS",
        content: <p>We do not control the price, inventory, delivery, returns, data practices, or listing accuracy of an unaffiliated seller. Issues with an unauthorized purchase should first be directed to that seller and the relevant marketplace or payment provider.</p>,
      },
      {
        title: "COUNTERFEIT OR ALTERED PRODUCTS",
        content: <p>Counterfeit, stolen, materially altered, or serial-number-removed products are not eligible for our voluntary warranty. We may ask for seller details and photographs to assess authenticity. We do not require you to surrender property without a lawful basis.</p>,
      },
      {
        title: "NO IMPLIED AFFILIATION",
        content: <p>A reseller’s use of a product name, image, compatibility statement, or keyword does not by itself establish sponsorship, endorsement, or authorization. Third-party brands remain owned by their respective holders.</p>,
      },
      {
        title: "REPORT A LISTING",
        content: <p>To ask about a seller or report a suspected counterfeit listing, email <EmailLink subject="Authenticity report" /> with the URL, seller name, purchase evidence if applicable, and clear product images. Do not send sensitive payment credentials.</p>,
      },
    ],
  },
  {
    slug: "acceptable-use",
    navTitle: "Acceptable use",
    title: ["ACCEPTABLE", "USE"],
    summary: "Rules that protect customers, store services, accounts, and checkout from misuse.",
    intro: "You may use the store for lawful personal shopping, product research, order management, and support. Use that harms others or interferes with the service is prohibited.",
    sections: [
      {
        title: "PROHIBITED CONDUCT",
        content: <ul>
          <li>fraud, impersonation, unlawful purchases, or unauthorized payment use;</li>
          <li>malware, security circumvention, denial-of-service activity, or destructive testing;</li>
          <li>automated purchasing, scraping, or traffic that places an unreasonable load on the store;</li>
          <li>harassment, threats, hate, exploitation, or submission of unlawful material; and</li>
          <li>infringement, counterfeit activity, promotion abuse, or unauthorized commercial resale.</li>
        </ul>,
      },
      {
        title: "ACCOUNTS & ACCESS",
        content: <p>If account features are available, keep credentials confidential and provide accurate information. You are responsible for activity you authorize. Tell us promptly if you suspect unauthorized access.</p>,
      },
      {
        title: "AUTOMATION & DATA",
        content: <p>Search-engine indexing and ordinary browser use are permitted. Other bots, bulk extraction, model training on restricted store content, inventory hoarding, or automated checkout require prior written permission.</p>,
      },
      {
        title: "ENFORCEMENT",
        content: <p>We may rate-limit traffic, cancel affected orders, restrict access, preserve evidence, or report unlawful conduct when reasonably necessary. Enforcement will be proportionate where practical and does not remove mandatory consumer remedies for an otherwise valid purchase.</p>,
      },
      {
        title: "QUESTIONS",
        content: <p>For permission or to report misuse, contact <EmailLink subject="Acceptable use question" />. Security vulnerabilities should follow the <Link to="/policies/security">Security Disclosure Policy</Link>.</p>,
      },
    ],
  },
];

const existingPolicies = [
  { title: "Privacy Policy", path: "/privacy", summary: "Personal information, use, sharing, retention, and privacy rights." },
  { title: "Terms of Service", path: "/terms", summary: "Rules for using the store and purchasing products." },
  { title: "Cookie Policy", path: "/cookies", summary: "Cookies, browser storage, measurement, and available controls." },
  { title: "Shipping Policy", path: "/shipping", summary: "Processing, tracking, delivery, customs, delays, and claims." },
  { title: "Returns & Exchanges", path: "/returns", summary: "Eligibility, exclusions, return steps, refunds, and exchanges." },
];

export function PolicyCenter() {
  return (
    <main className="policy-page policy-center">
      <header className="policy-page__header">
        <p className="policy-page__eyebrow">LEGAL &amp; SUPPORT / {siteConfig.wordmark}</p>
        <h1>POLICY<br />CENTER</h1>
        <p className="policy-page__lede">Clear rules for shopping, payment, delivery, product support, privacy, accessibility, and responsible use.</p>
        <div className="policy-page__meta">
          <span>LAST REVIEWED {POLICY_EFFECTIVE_DATE}</span>
          <span>QUESTIONS: <EmailLink /></span>
        </div>
      </header>

      <section className="policy-center__body" aria-labelledby="policy-center-title">
        <div className="policy-center__intro">
          <p id="policy-center-title">These policies are designed for the {siteConfig.wordmark} store and are written in our own language. Product- or promotion-specific terms shown before purchase also apply. Nothing in these pages limits rights that cannot be excluded under applicable law.</p>
        </div>

        <div className="policy-center__group">
          <div className="policy-center__heading"><span>01</span><h2>CORE STORE POLICIES</h2></div>
          <div className="policy-center__grid">
            {existingPolicies.map((policy) => (
              <Link className="policy-center__card" to={policy.path} key={policy.path}>
                <span>READ POLICY</span><h3>{policy.title}</h3><p>{policy.summary}</p><b aria-hidden="true">↗</b>
              </Link>
            ))}
          </div>
        </div>

        <div className="policy-center__group">
          <div className="policy-center__heading"><span>02</span><h2>ADDITIONAL POLICIES</h2></div>
          <div className="policy-center__grid">
            {legalPolicies.map((policy) => (
              <Link className="policy-center__card" to={`/policies/${policy.slug}`} key={policy.slug}>
                <span>READ POLICY</span><h3>{policy.navTitle}</h3><p>{policy.summary}</p><b aria-hidden="true">↗</b>
              </Link>
            ))}
          </div>
        </div>

        <div className="policy-center__contact">
          <div><span>NEED CLARIFICATION?</span><h2>ASK BEFORE<br />YOU ORDER.</h2></div>
          <p>Contact <EmailLink /> and tell us which policy or product you are asking about. Keep your order number available for an existing purchase.</p>
        </div>
      </section>
    </main>
  );
}

export function LegalPolicy() {
  const { policySlug } = useParams();
  const policy = legalPolicies.find((item) => item.slug === policySlug);

  if (!policy) return <Navigate to="/policies" replace />;

  return (
    <main className="policy-page legal-policy">
      <header className="policy-page__header">
        <p className="policy-page__eyebrow">POLICY / {siteConfig.wordmark}</p>
        <h1>{policy.title.map((line, index) => <React.Fragment key={line}>{index > 0 && <br />}{line}</React.Fragment>)}</h1>
        <p className="policy-page__lede">{policy.summary}</p>
        <div className="policy-page__meta">
          <span>EFFECTIVE {POLICY_EFFECTIVE_DATE}</span>
          <span>CONTACT: <EmailLink /></span>
        </div>
      </header>

      <div className="policy-page__layout">
        <aside className="policy-page__toc">
          <p>ON THIS PAGE</p>
          <nav aria-label={`${policy.navTitle} sections`}>
            {policy.sections.map((section, index) => (
              <a href={`#${policy.slug}-${index + 1}`} key={section.title}>
                <span>{String(index + 1).padStart(2, "0")}</span>{section.title.replace(/&/g, "and").toLowerCase()}
              </a>
            ))}
          </nav>
        </aside>

        <article className="policy-page__content">
          <div className="policy-page__intro"><p>{policy.intro}</p></div>
          {policy.sections.map((section, index) => (
            <section className="policy-page__section" id={`${policy.slug}-${index + 1}`} key={section.title}>
              <span className="policy-page__section-number">{String(index + 1).padStart(2, "0")}</span>
              <h2>{section.title}</h2>
              {section.content}
            </section>
          ))}
          <nav className="policy-page__related" aria-label="Related policies">
            <span>RELATED</span>
            <Link to="/policies">ALL POLICIES</Link>
            <Link to="/privacy">PRIVACY</Link>
            <Link to="/terms">TERMS</Link>
          </nav>
        </article>
      </div>
    </main>
  );
}

export { legalPolicies };
